import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function CyberSecurityPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    securityAreas: [] as string[],
    contactMethod: '',
    message: '',
  });
  const [messageLength, setMessageLength] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'message') {
      setMessageLength(value.length);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      securityAreas: checked
        ? [...prev.securityAreas, value]
        : prev.securityAreas.filter(area => area !== value)
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, contactMethod: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formBody = new URLSearchParams();
      formBody.append('firstName', formData.firstName);
      formBody.append('lastName', formData.lastName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('industry', formData.industry);
      formBody.append('securityAreas', formData.securityAreas.join(', '));
      formBody.append('contactMethod', formData.contactMethod);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d50s13g9ei8qjpb9fn8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          industry: '',
          securityAreas: [],
          contactMethod: '',
          message: '',
        });
        setMessageLength(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section 
        className="relative w-full h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/dda505e97e0f1337dd5f555f9dfcd3aa.jpg)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-800/75 to-transparent"></div>
        <div className="container mx-auto px-4 h-full flex items-center relative">
          <div className="w-full max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Enterprise-Grade Cybersecurity Solutions
            </h1>
            <p className="text-lg text-gray-100 mb-8">
              Protect your business with comprehensive security solutions designed to safeguard your critical assets from evolving cyber threats.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer">
                Get Security Assessment
              </button>
              <button className="bg-white text-gray-800 px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                Contact Security Team
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Security Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Security Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our enterprise security services help you identify vulnerabilities, protect against threats, and respond effectively to security incidents.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 transition-all hover:shadow-lg hover:-translate-y-1">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-shield-keyhole-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Threat Assessment &amp; Monitoring</h3>
              <p className="text-gray-600 mb-4">
                Continuous monitoring of your network and systems to detect and respond to security threats in real-time.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">24/7 Security Operations Center</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Threat Intelligence Integration</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Vulnerability Management</span>
                </li>
              </ul>
              <a href="#" className="text-primary font-medium flex items-center hover:underline">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Service Card 2 */}
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 transition-all hover:shadow-lg hover:-translate-y-1">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-bug-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Penetration Testing &amp; Audits</h3>
              <p className="text-gray-600 mb-4">
                Identify vulnerabilities in your systems before attackers do with comprehensive security testing.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Network Penetration Testing</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Web Application Security Testing</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Compliance Audits</span>
                </li>
              </ul>
              <a href="#" className="text-primary font-medium flex items-center hover:underline">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Service Card 3 */}
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 transition-all hover:shadow-lg hover:-translate-y-1">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-alarm-warning-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Security Incident Response</h3>
              <p className="text-gray-600 mb-4">
                Rapid response to security incidents with expert remediation and recovery services.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Incident Response Planning</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Breach Investigation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Digital Forensics</span>
                </li>
              </ul>
              <a href="#" className="text-primary font-medium flex items-center hover:underline">
                Learn More
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Security Products */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Enterprise Security Products</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Deploy industry-leading security technologies to protect your network, endpoints, and data from sophisticated cyber threats.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Product 1 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1">
              <div className="h-48 bg-gray-100 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Enterprise%20network%20firewall%20hardware%20device%20with%20multiple%20ports%20and%20LED%20indicators.%20Professional%20next-generation%20firewall%20appliance%20on%20simple%20clean%20white%20background.%20Modern%20black%20metal%20chassis%20with%20blue%20LED%20lights%2C%20network%20ports%2C%20and%20enterprise%20security%20hardware%20design.&width=400&height=300&seq=firewall1&orientation=landscape"
                  alt="Enterprise Firewalls"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Enterprise Firewalls</h3>
                <p className="text-gray-600 mb-4">
                  Next-generation firewall solutions with advanced threat protection, application control, and intrusion prevention.
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">From €3,199</span>
                </div>
              </div>
            </div>

            {/* Product 2 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1">
              <div className="h-48 bg-gray-100 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Endpoint%20protection%20software%20interface%20with%20security%20dashboard%20and%20antivirus%20shield%20icons.%20Modern%20cybersecurity%20software%20screen%20showing%20device%20protection%20status%20on%20simple%20clean%20background.%20Blue%20and%20white%20interface%20with%20security%20indicators%20and%20protection%20status.&width=400&height=300&seq=endpoint1&orientation=landscape"
                  alt="Endpoint Protection"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Endpoint Protection</h3>
                <p className="text-gray-600 mb-4">
                  Comprehensive endpoint security with anti-malware, device control, and behavioral analysis capabilities.
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">
                    €41 <span className="text-sm text-gray-500">/ device / year</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Product 3 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1">
              <div className="h-48 bg-gray-100 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Intrusion%20detection%20system%20monitoring%20dashboard%20with%20network%20traffic%20analysis%20and%20threat%20alerts.%20Professional%20IDS%20software%20interface%20showing%20real-time%20security%20monitoring%20on%20simple%20clean%20background.%20Dark%20interface%20with%20red%20alert%20indicators%20and%20network%20visualization.&width=400&height=300&seq=ids1&orientation=landscape"
                  alt="Intrusion Detection Systems"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Intrusion Detection Systems</h3>
                <p className="text-gray-600 mb-4">
                  Advanced network monitoring and threat detection to identify and alert on suspicious activities.
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">From €2,519</span>
                </div>
              </div>
            </div>

            {/* Product 4 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1">
              <div className="h-48 bg-gray-100 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Access%20control%20system%20with%20biometric%20authentication%20and%20multi-factor%20security.%20Modern%20identity%20management%20interface%20with%20fingerprint%20scanner%20and%20security%20badges%20on%20simple%20clean%20background.%20Professional%20access%20control%20hardware%20with%20blue%20LED%20indicators.&width=400&height=300&seq=access1&orientation=landscape"
                  alt="Access Control Solutions"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Access Control Solutions</h3>
                <p className="text-gray-600 mb-4">
                  Identity and access management systems with multi-factor authentication and privileged access controls.
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">Custom Quote</span>
                  <button className="bg-primary text-white px-4 py-2 rounded-button whitespace-nowrap text-sm hover:bg-primary/90 transition-colors cursor-pointer">
                    Request Info
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-10">
            <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
              View All Security Products
            </button>
          </div>
        </div>
      </section>

      {/* Expert Security Consulting Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <span className="text-primary font-semibold mb-2 block">SECURITY CONSULTING</span>
              <h2 className="text-3xl font-bold mb-6">Expert Security Consulting Services</h2>
              <p className="text-gray-600 mb-6">
                Our team of certified security experts provides strategic guidance to help you build a robust security program aligned with your business objectives and compliance requirements.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-file-search-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Security Program Assessment</h3>
                    <p className="text-gray-600">
                      Comprehensive evaluation of your current security posture with actionable recommendations.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-building-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Security Architecture Design</h3>
                    <p className="text-gray-600">
                      Design and implementation of security controls and technologies tailored to your environment.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-government-line"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Compliance &amp; Governance</h3>
                    <p className="text-gray-600">
                      Guidance on meeting regulatory requirements including GDPR, HIPAA, PCI DSS, and ISO 27001.
                    </p>
                  </div>
                </div>
              </div>
              <button className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer">
                Schedule Consultation
              </button>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20cybersecurity%20consulting%20team%20working%20in%20modern%20security%20operations%20center%20with%20multiple%20monitors%20showing%20network%20security%20dashboards.%20Enterprise%20security%20experts%20analyzing%20threat%20data%20in%20high-tech%20environment.%20Blue%20and%20white%20color%20scheme%20with%20security%20monitoring%20screens%20and%20professional%20atmosphere.&width=800&height=600&seq=consultinging1&orientation=landscape"
                  alt="Security Consulting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Today's Threat Landscape */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Understanding Today's Threat Landscape</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Stay informed about the evolving cybersecurity threats facing businesses today and how Thyamis can help you mitigate these risks.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Cyber Threat Trends */}
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Cyber Threat Trends</h3>
              <div className="w-full h-80 bg-gray-50 rounded-lg flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Cybersecurity%20threat%20trends%20chart%20showing%20increasing%20cyber%20attacks%20over%20time.%20Professional%20data%20visualization%20with%20line%20graphs%20displaying%20malware%2C%20ransomware%2C%20and%20phishing%20attack%20trends.%20Blue%20and%20red%20color%20scheme%20on%20clean%20white%20background%20with%20clear%20statistics.&width=600&height=400&seq=threattrends1&orientation=landscape"
                  alt="Cyber Threat Trends Chart"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Common Attack Vectors */}
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Common Attack Vectors</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-red-500">
                      <i className="ri-spam-2-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Phishing &amp; Social Engineering</h4>
                    <p className="text-gray-600">
                      Sophisticated attacks that manipulate users into revealing sensitive information or installing malware.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-orange-500">
                      <i className="ri-lock-password-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Ransomware</h4>
                    <p className="text-gray-600">
                      Malicious software that encrypts data and demands payment for decryption keys, causing business disruption.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-yellow-600">
                      <i className="ri-user-follow-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Insider Threats</h4>
                    <p className="text-gray-600">
                      Security risks posed by employees, contractors, or partners with legitimate access to systems.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <div className="w-6 h-6 flex items-center justify-center text-purple-600">
                      <i className="ri-cloud-line"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">Cloud Security Vulnerabilities</h4>
                    <p className="text-gray-600">
                      Misconfigurations and security gaps in cloud environments that can be exploited by attackers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stay Protected */}
          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="w-full md:w-1/2">
                <h3 className="text-xl font-semibold mb-4">Stay Protected with Thyamis Security</h3>
                <p className="text-gray-600 mb-4">
                  Our comprehensive security approach combines advanced technology, expert services, and continuous monitoring to protect your business from evolving threats.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Proactive threat hunting and monitoring</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Regular security assessments and testing</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Employee security awareness training</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Incident response planning and support</span>
                  </li>
                </ul>
                <button className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer">
                  Get Protected Now
                </button>
              </div>
              <div className="w-full md:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=Cybersecurity%20protection%20shield%20visualization%20with%20digital%20lock%20and%20security%20elements%20protecting%20business%20data.%20The%20image%20should%20show%20a%20protective%20shield%20around%20business%20assets%20with%20security%20technology%20elements%20like%20locks%2C%20encryption%20symbols%2C%20and%20protection%20mechanisms.%20Blue%20and%20white%20color%20scheme%20with%20glowing%20security%20elements%20on%20a%20clean%20background.&width=600&height=400&seq=protect1&orientation=landscape"
                  alt="Cybersecurity Protection"
                  className="rounded-lg shadow-md w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Request Security Assessment Form */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex flex-col md:flex-row">
              {/* Left Column - Info */}
              <div className="w-full md:w-5/12 bg-primary p-8 text-white">
                <h2 className="text-2xl font-bold mb-4">Request a Security Assessment</h2>
                <p className="mb-6">
                  Our security experts will analyze your current security posture and provide recommendations to strengthen your defenses.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-shield-check-line"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Comprehensive Analysis</h3>
                      <p className="text-blue-100 text-sm">
                        Thorough evaluation of your security controls, policies, and vulnerabilities.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-file-list-3-line"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Detailed Reporting</h3>
                      <p className="text-blue-100 text-sm">
                        Clear documentation of findings with prioritized recommendations.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-road-map-line"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Remediation Roadmap</h3>
                      <p className="text-blue-100 text-sm">
                        Strategic plan to address security gaps and improve your security posture.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-white/20 pt-6">
                  <p className="font-semibold mb-2">Contact our security team directly:</p>
                  <div className="flex items-center mb-2">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-phone-line"></i>
                    </div>
                    <span>+31 (0)616300011</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-mail-line"></i>
                    </div>
                    <span>info@thyamis.com</span>
                  </div>
                </div>
              </div>

              {/* Right Column - Form */}
              <div className="w-full md:w-7/12 p-8">
                <form id="security-assessment-form" data-readdy-form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder="Enter first name"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder="Enter last name"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name
                      </label>
                      <input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                        placeholder="Enter company name"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
                        Industry
                      </label>
                      <div className="relative">
                        <select
                          id="industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors appearance-none pr-8"
                          required
                        >
                          <option value="" disabled>
                            Select your industry
                          </option>
                          <option value="Financial Services">Financial Services</option>
                          <option value="Healthcare">Healthcare</option>
                          <option value="Manufacturing">Manufacturing</option>
                          <option value="Retail">Retail</option>
                          <option value="Technology">Technology</option>
                          <option value="Government">Government</option>
                          <option value="Education">Education</option>
                          <option value="Other">Other</option>
                        </select>
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <div className="w-4 h-4 flex items-center justify-center text-gray-500">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Security Areas of Interest
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="securityAreas"
                          value="Network Security"
                          checked={formData.securityAreas.includes('Network Security')}
                          onChange={handleCheckboxChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">Network Security</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="securityAreas"
                          value="Endpoint Security"
                          checked={formData.securityAreas.includes('Endpoint Security')}
                          onChange={handleCheckboxChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">Endpoint Security</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="securityAreas"
                          value="Cloud Security"
                          checked={formData.securityAreas.includes('Cloud Security')}
                          onChange={handleCheckboxChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">Cloud Security</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="securityAreas"
                          value="Data Protection"
                          checked={formData.securityAreas.includes('Data Protection')}
                          onChange={handleCheckboxChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">Data Protection</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="securityAreas"
                          value="Compliance & Governance"
                          checked={formData.securityAreas.includes('Compliance & Governance')}
                          onChange={handleCheckboxChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">Compliance &amp; Governance</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="securityAreas"
                          value="Security Awareness Training"
                          checked={formData.securityAreas.includes('Security Awareness Training')}
                          onChange={handleCheckboxChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">Security Awareness Training</span>
                      </label>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Contact Method
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="Email"
                          checked={formData.contactMethod === 'Email'}
                          onChange={handleRadioChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">Email</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="Phone"
                          checked={formData.contactMethod === 'Phone'}
                          onChange={handleRadioChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">Phone</span>
                      </label>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Information
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                      placeholder="Please share any specific security concerns or requirements (max 500 characters)"
                    ></textarea>
                    <div className="text-right text-sm text-gray-500 mt-1">
                      <span>{messageLength}</span>/500 characters
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium w-full hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Security Consultation'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                      Thank you! Your security assessment request has been submitted successfully. Our team will contact you soon.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                      Sorry, there was an error submitting your request. Please try again or contact us directly.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}